import { useState, useEffect, useCallback } from "react";
import { 
  Wallet, 
  TrendingUp, 
  TrendingDown, 
  Building2,
  ArrowRight,
  RefreshCw,
  Receipt,
  Calculator,
  ShoppingBag,
  ArrowUpRight,
  ArrowDownRight,
  CreditCard,
  FileText,
  Download,
  FileDown,
  AlertTriangle,
  Bell,
  FolderKanban,
  ChevronLeft,
  ChevronRight,
  Filter
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { DatePicker } from "../components/DatePicker";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { getDashboardSummary, formatCurrency, formatDate, getProjectSummary, getStatutoryMonthly, toggleStatutoryPaid, getBankAccounts } from "../lib/api";
import { COMPANY_OPTIONS, getCompanyLabel, getCompanyLogo, COMPANY_COLORS } from "../lib/companies";
import { toast } from "sonner";
import { downloadFile } from "../lib/download";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Cell,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";

// Brand-aligned chart colors
const CHART_COLORS = ["#1E3A5F", "#F5A623", "#475569", "#1E3A5F99", "#F5A62399"];
const COMPANY_CHART_COLORS = {
  "empact-studio": "#1E3A5F",
  "event-resource": "#F5A623", 
  "vijay-sabhlok": "#475569",
};

// Custom tooltip style with high contrast for readability
const chartTooltipStyle = {
  backgroundColor: "#1E3A5F",
  border: "2px solid #F5A623",
  borderRadius: "8px",
  color: "#FFFFFF",
  fontSize: "13px",
  fontWeight: "500",
  padding: "8px 12px",
  boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
};

const KPICard = ({ title, value, subtitle, icon: Icon, trend, trendValue, variant = "default", linkTo }) => {
  const variants = {
    default: "bg-white border-slate-200 hover:border-brand-orange/30",
    primary: "bg-brand-navy text-white",
    accent: "bg-brand-orange/10 border-brand-orange/30",
    success: "bg-emerald-50 border-emerald-200",
    danger: "bg-red-50 border-red-200",
    warning: "bg-amber-50 border-amber-200",
  };

  const iconVariants = {
    default: "bg-brand-navy/10 text-brand-navy",
    primary: "bg-white/20 text-white",
    accent: "bg-brand-orange/20 text-brand-orange",
    success: "bg-emerald-100 text-emerald-600",
    danger: "bg-red-100 text-red-600",
    warning: "bg-amber-100 text-amber-600",
  };

  const cardContent = (
    <Card className={`${variants[variant] || variants.default} border h-full ${linkTo ? 'cursor-pointer hover:shadow-lg transition-all duration-200' : ''}`}>
      <CardContent className="p-3 sm:p-5 h-full flex flex-col">
        <div className="flex items-start justify-between flex-1">
          <div className="flex-1 min-w-0 overflow-hidden">
            <div className="flex items-center gap-1.5 mb-0.5 sm:mb-1">
              <span className={`text-[11px] sm:text-sm font-medium truncate ${variant === 'primary' ? 'text-white/80' : 'text-slate-500'}`}>{title}</span>
              {trendValue && (
                <Badge variant="secondary" className={`text-[10px] px-1 py-0 flex-shrink-0 ${trend === 'up' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                  {trend === 'up' ? <ArrowUpRight className="w-3 h-3 inline" /> : <ArrowDownRight className="w-3 h-3 inline" />}
                  {trendValue}
                </Badge>
              )}
            </div>
            <p className={`text-sm sm:text-lg md:text-xl font-bold tabular-nums leading-tight ${variant === 'primary' ? 'text-white' : 'text-brand-navy'}`}>{value}</p>
            {subtitle && (
              <p className={`text-[10px] sm:text-xs mt-0.5 sm:mt-1 truncate ${variant === 'primary' ? 'text-white/60' : 'text-slate-400'}`}>
                {subtitle}
                {linkTo && <ArrowRight className="w-3 h-3 inline ml-1" />}
              </p>
            )}
          </div>
          <div className={`hidden sm:flex p-1.5 sm:p-2.5 rounded-xl flex-shrink-0 ml-2 ${iconVariants[variant] || iconVariants.default}`}>
            <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (linkTo) {
    return <Link to={linkTo} data-testid={`kpi-link-${title.toLowerCase().replace(/\s+/g, '-')}`} className="block h-full">{cardContent}</Link>;
  }
  return cardContent;
};

const StatusBadge = ({ status }) => {
  const statusClasses = {
    pending: "bg-amber-100 text-amber-700",
    paid: "bg-emerald-100 text-emerald-700",
    overdue: "bg-red-100 text-red-700",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusClasses[status] || statusClasses.pending}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

export default function Dashboard() {
  const { canEdit } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState("all");
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState("pdf");
  const [exportPeriod, setExportPeriod] = useState("current_month");
  const [exportScope, setExportScope] = useState("summary");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [exportCompany, setExportCompany] = useState("all");
  const [alertFilters, setAlertFilters] = useState({
    statutory: true,
    credit_card: true,
    payable: true,
    receivable: true
  });
  const [showAllAlerts, setShowAllAlerts] = useState(false);
  const [error, setError] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [projectSummary, setProjectSummary] = useState(null);
  const [statutoryModal, setStatutoryModal] = useState(null); // "tds" | "gst" | null
  const [statutoryData, setStatutoryData] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [tdsBankSelector, setTdsBankSelector] = useState({ open: false, month: null, bankId: "" });
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const navigateMonth = (direction) => {
    setSelectedMonth(prev => {
      const [y, m] = prev.split('-').map(Number);
      const d = new Date(y, m - 1 + direction, 1);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    });
  };

  const formatMonthLabel = (monthStr) => {
    const [y, m] = monthStr.split('-').map(Number);
    return new Date(y, m - 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  };

  const fetchData = useCallback(async (company = "all") => {
    try {
      setLoading(true);
      const response = await getDashboardSummary(company, selectedMonth);
      setSummary(response.data);
      setError(null);
    } catch (error) {
      console.error("Dashboard load error:", error);
      setError("Failed to load dashboard data");
    }
    try {
      const projRes = await getProjectSummary(company);
      setProjectSummary(projRes.data);
    } catch (error) {
      // Projects summary failure shouldn't block dashboard
    }
    setLoading(false);
  }, [selectedMonth]);

  const openStatutoryModal = async (type) => {
    try {
      const [res, bankRes] = await Promise.all([
        getStatutoryMonthly(selectedCompany),
        getBankAccounts(),
      ]);
      setStatutoryData(res.data);
      setBankAccounts(bankRes.data);
      setStatutoryModal(type);
    } catch (error) {
      toast.error("Failed to load statutory data");
    }
  };

  const handleTogglePaid = async (type, month, bankAccountId = "") => {
    try {
      await toggleStatutoryPaid(type, month, selectedCompany, bankAccountId);
      // Refresh both statutory data and dashboard
      const [statRes] = await Promise.all([
        getStatutoryMonthly(selectedCompany),
      ]);
      setStatutoryData(statRes.data);
      fetchData(selectedCompany);
      toast.success(`${type.toUpperCase()} for ${month} updated`);
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleTdsMarkPaid = (month) => {
    setTdsBankSelector({ open: true, month, bankId: "" });
  };

  const confirmTdsPaid = async () => {
    if (!tdsBankSelector.bankId) {
      toast.error("Please select a bank account for TDS payment");
      return;
    }
    await handleTogglePaid("tds", tdsBankSelector.month, tdsBankSelector.bankId);
    setTdsBankSelector({ open: false, month: null, bankId: "" });
  };

  useEffect(() => {
    fetchData(selectedCompany);
  }, [selectedCompany, selectedMonth, fetchData]);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => fetchData(selectedCompany), 300000);
    return () => clearInterval(interval);
  }, [selectedCompany, fetchData]);

  // Live clock — update every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleExport = async () => {
    try {
      setExporting(true);
      const params = new URLSearchParams({
        format: exportFormat,
        period: exportPeriod,
        scope: exportScope,
        company: exportCompany,
      });
      if (exportPeriod === "custom") {
        if (!customStartDate || !customEndDate) {
          toast.error("Please select both start and end dates");
          setExporting(false);
          return;
        }
        params.set("start_date", customStartDate);
        params.set("end_date", customEndDate);
      }
      const url = `/api/dashboard/export?${params.toString()}`;
      const filename = `dashboard_summary_${new Date().toISOString().split('T')[0]}.${exportFormat}`;
      
      // Close dialog FIRST so its overlay doesn't block the download
      setExportDialogOpen(false);
      
      const success = await downloadFile(url, filename);
      if (success) {
        toast.success(`${exportFormat.toUpperCase()} downloaded successfully`);
      }
    } catch (error) {
      toast.error("Failed to export dashboard");
    } finally {
      setExporting(false);
    }
  };

  // Prepare expense breakdown data for pie chart
  const expenseChartData = summary?.expense_breakdown
    ? Object.entries(summary.expense_breakdown).map(([name, value]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1).replace(/-/g, ' '),
        value,
      }))
    : [];

  // Prepare company-wise data for bar chart
  const companyData = COMPANY_OPTIONS.map((company) => {
    const invoices = summary?.recent_invoices?.filter(inv => inv.company === company.value) || [];
    const payables = summary?.recent_payables?.filter(pay => pay.company === company.value) || [];
    return {
      name: company.label.split(' ')[0], // Short name
      fullName: company.label,
      receivables: invoices.reduce((sum, inv) => sum + (inv.total_amount || 0), 0),
      payables: payables.reduce((sum, pay) => sum + (pay.total_amount || 0), 0),
    };
  });

  // GST summary data for visualization
  const gstData = [
    { name: 'Collected', value: summary?.gst_summary?.collected?.total || 0, color: '#10B981' },
    { name: 'ITC', value: summary?.gst_summary?.paid?.total || 0, color: '#3B82F6' },
  ];

  // Filter alerts based on selected categories
  const filteredAlerts = (summary?.alerts || []).filter(alert => {
    return alertFilters[alert.category];
  });

  const displayedAlerts = showAllAlerts ? filteredAlerts : filteredAlerts.slice(0, 5);

  const toggleAlertFilter = (category) => {
    setAlertFilters(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Get alert counts by severity
  const criticalCount = filteredAlerts.filter(a => a.severity === 'critical').length;
  const overdueCount = filteredAlerts.filter(a => a.severity === 'overdue').length;
  const warningCount = filteredAlerts.filter(a => a.severity === 'warning').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="w-8 h-8 text-brand-orange animate-spin" />
          <p className="text-sm text-slate-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Company Logos Strip */}
      <div className="flex items-center gap-3 sm:gap-6 py-3 px-3 sm:px-4 bg-white rounded-xl shadow-sm border border-slate-100 overflow-x-auto sm:justify-center">
        {COMPANY_OPTIONS.map((company) => (
          <button
            key={company.value}
            onClick={() => setSelectedCompany(company.value)}
            className={`flex items-center gap-2 px-2 sm:px-3 py-2 rounded-lg transition-all duration-200 flex-shrink-0 ${
              selectedCompany === company.value 
                ? 'bg-brand-navy/10 ring-2 ring-brand-navy shadow-md scale-105' 
                : 'hover:bg-slate-50 hover:shadow-sm'
            }`}
            data-testid={`company-logo-${company.value}`}
          >
            {company.logo && (
              <img 
                src={company.logo} 
                alt={company.label} 
                className="h-8 sm:h-10 w-auto object-contain"
              />
            )}
          </button>
        ))}
        {selectedCompany !== "all" && (
          <button
            onClick={() => setSelectedCompany("all")}
            className="ml-2 px-3 py-1 text-xs text-slate-500 hover:text-brand-navy bg-slate-100 hover:bg-slate-200 rounded-full transition-colors flex-shrink-0"
          >
            View All
          </button>
        )}
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5" data-testid="dashboard-datetime">
            {currentTime.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            {" · "}
            {currentTime.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true }).toUpperCase()}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center w-full sm:w-auto">
          {/* Company Filter */}
          <Select value={selectedCompany} onValueChange={setSelectedCompany}>
            <SelectTrigger className="w-full sm:w-48" data-testid="dashboard-company-filter">
              <SelectValue>
                {selectedCompany === "all" ? (
                  "All Companies"
                ) : (
                  <div className="flex items-center gap-2">
                    {getCompanyLogo(selectedCompany) && (
                      <img 
                        src={getCompanyLogo(selectedCompany)} 
                        alt="" 
                        className="h-5 w-auto object-contain"
                      />
                    )}
                    <span className="truncate">{getCompanyLabel(selectedCompany)}</span>
                  </div>
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                <span className="font-medium">All Companies</span>
              </SelectItem>
              {COMPANY_OPTIONS.map((company) => (
                <SelectItem key={company.value} value={company.value}>
                  <div className="flex items-center gap-2">
                    {company.logo && (
                      <img 
                        src={company.logo} 
                        alt="" 
                        className="h-5 w-auto object-contain"
                      />
                    )}
                    <span>{company.label}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button 
            onClick={() => setExportDialogOpen(true)} 
            variant="outline" 
            size="sm"
            className="gap-2 border-brand-orange text-brand-orange hover:bg-brand-orange/10"
            data-testid="export-dashboard-btn"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Export</span>
          </Button>
          <Button 
            onClick={() => fetchData(selectedCompany)} 
            variant="outline" 
            size="sm"
            className="gap-2"
            disabled={loading}
            data-testid="refresh-dashboard"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
        </div>
      </div>

      {/* Company Badge */}
      {selectedCompany !== "all" && (
        <div className="flex items-center gap-3">
          {getCompanyLogo(selectedCompany) && (
            <img 
              src={getCompanyLogo(selectedCompany)} 
              alt={getCompanyLabel(selectedCompany)} 
              className="h-8 w-auto object-contain"
            />
          )}
          <Badge className={`${COMPANY_COLORS[selectedCompany]} px-3 py-1`}>
            Viewing: {getCompanyLabel(selectedCompany)}
          </Badge>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setSelectedCompany("all")}
            className="text-xs text-slate-500 hover:text-slate-700"
          >
            Clear filter
          </Button>
        </div>
      )}

      {/* Month Navigation */}
      <div className="flex items-center justify-center gap-4 py-3 px-4 bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="month-navigator">
        <Button variant="ghost" size="icon" onClick={() => navigateMonth(-1)} className="h-8 w-8 hover:bg-slate-100" data-testid="month-prev">
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <h2 className="text-base sm:text-lg font-bold text-brand-navy min-w-[180px] text-center" data-testid="month-label">
          {formatMonthLabel(selectedMonth)}
        </h2>
        <Button variant="ghost" size="icon" onClick={() => navigateMonth(1)} className="h-8 w-8 hover:bg-slate-100" data-testid="month-next">
          <ChevronRight className="w-5 h-5" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="ml-2 text-xs"
          onClick={() => {
            const now = new Date();
            setSelectedMonth(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
          }}
          data-testid="month-today"
        >
          Today
        </Button>
      </div>

      {/* Bank Accounts by Type (for Vijay Sabhlok or when filtered) */}
      {selectedCompany !== "all" && summary?.bank_accounts_by_type && Object.keys(summary.bank_accounts_by_type).length > 0 && (
        <Card className="bg-gradient-to-r from-brand-navy to-brand-navy/90">
          <CardContent className="p-5">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Bank Accounts - {getCompanyLabel(selectedCompany)}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(summary.bank_accounts_by_type).map(([type, data]) => (
                <div key={type} className="bg-white/10 rounded-lg p-4">
                  <p className="text-white/70 text-xs uppercase tracking-wide mb-1">
                    {type === 'current' ? 'Current A/c' : type === 'savings' ? 'Savings A/c' : type}
                  </p>
                  <p className="text-white text-sm sm:text-xl font-bold tabular-nums">
                    {formatCurrency(data.balance)}
                  </p>
                  <p className="text-white/50 text-xs mt-1">
                    {data.count} account{data.count > 1 ? 's' : ''}
                  </p>
                </div>
              ))}
              <div className="bg-brand-orange/20 rounded-lg p-4 border border-brand-orange/30">
                <p className="text-brand-orange text-xs uppercase tracking-wide mb-1">Total</p>
                <p className="text-white text-sm sm:text-xl font-bold tabular-nums">
                  {formatCurrency(summary.total_bank_balance || 0)}
                </p>
                <p className="text-white/50 text-xs mt-1">
                  {summary.bank_accounts?.length || 0} accounts
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Urgent Alerts Section */}
      {filteredAlerts.length > 0 && (
        <Card className="border-l-4 border-l-red-500 bg-gradient-to-r from-red-50 to-white">
          <CardContent className="p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-3 gap-2">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-full animate-pulse">
                  <Bell className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                    Urgent Alerts
                    <Badge variant="destructive" className="text-xs">
                      {filteredAlerts.length}
                    </Badge>
                  </h3>
                  <p className="text-xs text-slate-500">
                    {criticalCount > 0 && <span className="text-red-600 font-medium">{criticalCount} critical</span>}
                    {criticalCount > 0 && (overdueCount > 0 || warningCount > 0) && " · "}
                    {overdueCount > 0 && <span className="text-orange-600 font-medium">{overdueCount} overdue</span>}
                    {overdueCount > 0 && warningCount > 0 && " · "}
                    {warningCount > 0 && <span className="text-amber-600 font-medium">{warningCount} warning</span>}
                  </p>
                </div>
              </div>
              
              {/* Filter Toggles */}
              <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                <div className="flex items-center gap-1 text-xs flex-wrap">
                  <Filter className="w-3 h-3 text-slate-400" />
                  <button
                    onClick={() => toggleAlertFilter('statutory')}
                    className={`px-2 py-1 rounded-full transition-colors ${alertFilters.statutory ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-400'}`}
                  >
                    Statutory
                  </button>
                  <button
                    onClick={() => toggleAlertFilter('credit_card')}
                    className={`px-2 py-1 rounded-full transition-colors ${alertFilters.credit_card ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-400'}`}
                  >
                    Cards
                  </button>
                  <button
                    onClick={() => toggleAlertFilter('payable')}
                    className={`px-2 py-1 rounded-full transition-colors ${alertFilters.payable ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-400'}`}
                  >
                    Payables
                  </button>
                  <button
                    onClick={() => toggleAlertFilter('receivable')}
                    className={`px-2 py-1 rounded-full transition-colors ${alertFilters.receivable ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}
                  >
                    Overdue
                  </button>
                </div>
              </div>
            </div>

            {/* Alert List */}
            <div className="space-y-2">
              {displayedAlerts.map((alert, idx) => (
                <div 
                  key={`${alert.type}-${alert.message?.substring(0, 20)}-${idx}`}
                  className={`flex items-start sm:items-center justify-between p-2 sm:p-3 rounded-lg border gap-2 ${
                    alert.severity === 'critical' 
                      ? 'bg-red-50 border-red-200 animate-pulse' 
                      : alert.severity === 'overdue'
                        ? 'bg-orange-50 border-orange-200'
                        : 'bg-amber-50 border-amber-200'
                  }`}
                >
                  <div className="flex items-start sm:items-center gap-2 sm:gap-3 min-w-0 flex-1">
                    <div className={`p-1 sm:p-1.5 rounded-full flex-shrink-0 ${
                      alert.severity === 'critical' ? 'bg-red-200' : 
                      alert.severity === 'overdue' ? 'bg-orange-200' : 'bg-amber-200'
                    }`}>
                      <AlertTriangle className={`w-3 h-3 sm:w-4 sm:h-4 ${
                        alert.severity === 'critical' ? 'text-red-700' : 
                        alert.severity === 'overdue' ? 'text-orange-700' : 'text-amber-700'
                      }`} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs sm:text-sm font-medium text-slate-900 truncate">{alert.title}</p>
                      <p className="text-xs text-slate-500 truncate">{alert.description}</p>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 ml-1">
                    <p className={`text-sm font-bold tabular-nums ${
                      alert.severity === 'critical' ? 'text-red-700' : 
                      alert.severity === 'overdue' ? 'text-orange-700' : 'text-amber-700'
                    }`}>
                      {formatCurrency(alert.amount)}
                    </p>
                    <p className={`text-xs font-medium ${
                      alert.days_remaining <= 0 
                        ? 'text-red-600' 
                        : alert.days_remaining <= 3 
                          ? 'text-red-500' 
                          : 'text-amber-600'
                    }`}>
                      {alert.days_remaining < 0 
                        ? `${Math.abs(alert.days_remaining)} days overdue` 
                        : alert.days_remaining === 0 
                          ? 'Due Today!' 
                          : `${alert.days_remaining} days left`}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Show More/Less */}
            {filteredAlerts.length > 5 && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowAllAlerts(!showAllAlerts)}
                className="w-full mt-2 text-slate-600 hover:text-slate-900"
              >
                {showAllAlerts ? 'Show Less' : `Show All (${filteredAlerts.length})`}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Main KPIs Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <KPICard
          title="Bank Balance"
          value={formatCurrency(summary?.total_bank_balance || 0)}
          subtitle="All accounts"
          icon={Building2}
          variant="default"
          linkTo="/bank-accounts"
        />
        <KPICard
          title="Total Liabilities"
          value={formatCurrency(summary?.total_liabilities || 0)}
          subtitle="Payables + Variable + Fixed + GST + TDS"
          icon={TrendingDown}
          variant="danger"
        />
        <KPICard
          title="Current Cash"
          value={formatCurrency(summary?.total_cash_position || 0)}
          subtitle="Bank - Liabilities"
          icon={Wallet}
          variant={(summary?.total_cash_position || 0) >= 0 ? "success" : "danger"}
        />
        <KPICard
          title="Projected Cash"
          value={formatCurrency(summary?.projected_cash || 0)}
          subtitle="Current + Receivables"
          icon={TrendingUp}
          variant="accent"
        />
      </div>

      {/* Liabilities Breakdown */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard
          title="Receivables"
          value={formatCurrency(summary?.total_receivables || 0)}
          subtitle="Base amount (excl GST)"
          icon={FileText}
          variant="default"
          linkTo="/invoices"
        />
        <KPICard
          title="Payables"
          value={formatCurrency(summary?.total_payables || 0)}
          subtitle="Base amount (excl GST)"
          icon={CreditCard}
          variant="danger"
          linkTo="/payables"
        />
        <KPICard
          title="Variable Expenses"
          value={formatCurrency(summary?.total_variable_expenses || 0)}
          subtitle="Pending (excl GST)"
          icon={ShoppingBag}
          variant="danger"
          linkTo="/variable-expenses"
        />
        <KPICard
          title="Unpaid Fixed Expenses"
          value={formatCurrency(summary?.unpaid_fixed_expenses || 0)}
          subtitle="This month's pending"
          icon={Receipt}
          variant={(summary?.unpaid_fixed_expenses || 0) > 0 ? "danger" : "success"}
          linkTo="/fixed-expenses"
        />
      </div>

      {/* Statutory Dues */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        <div className="cursor-pointer" onClick={() => openStatutoryModal("gst")} data-testid="gst-payable-card">
          <KPICard
            title="GST Payable"
            value={formatCurrency(summary?.gst_to_govt || 0)}
            subtitle="Click to view monthly details"
            icon={Calculator}
            variant={(summary?.gst_to_govt || 0) > 0 ? "danger" : "success"}
          />
        </div>
        <div className="cursor-pointer" onClick={() => openStatutoryModal("tds")} data-testid="tds-payable-card">
          <KPICard
            title="TDS Payable"
            value={formatCurrency(summary?.tds_payable || 0)}
            subtitle="Click to view monthly details"
            icon={Calculator}
            variant={(summary?.tds_payable || 0) > 0 ? "danger" : "success"}
          />
        </div>
        <KPICard
          title="TDS Receivable"
          value={formatCurrency(summary?.tds_receivable || 0)}
          subtitle="TDS deducted by clients"
          icon={Calculator}
          variant={(summary?.tds_receivable || 0) > 0 ? "success" : "default"}
        />
        <KPICard
          title="Advances Outstanding"
          value={formatCurrency(summary?.employee_advances_outstanding || 0)}
          subtitle="Recoverable from employees"
          icon={ArrowDownRight}
          variant={(summary?.employee_advances_outstanding || 0) > 0 ? "warning" : "default"}
          linkTo="/fixed-expenses"
        />
      </div>

      {/* Run Rate Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Link to="/fixed-expenses" data-testid="run-rate-current-link">
          <Card className="bg-brand-navy text-white cursor-pointer hover:shadow-xl transition-all">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/70 text-sm">Run Rate (Current)</p>
                  <p className="text-2xl sm:text-3xl md:text-4xl font-bold mt-1">{summary?.run_rate_current || 0}</p>
                  <p className="text-white/50 text-sm mt-1">months runway</p>
                </div>
                <div className="text-right">
                  <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                    (summary?.run_rate_current || 0) >= 3 ? 'bg-emerald-500/20 text-emerald-300' : 
                    (summary?.run_rate_current || 0) >= 1 ? 'bg-brand-orange/30 text-brand-orange' : 
                    'bg-red-500/20 text-red-300'
                  }`}>
                    {(summary?.run_rate_current || 0) >= 3 ? 'Healthy' : (summary?.run_rate_current || 0) >= 1 ? 'Caution' : 'Critical'}
                  </div>
                  <p className="text-white/40 text-xs mt-2">Cash ÷ Monthly Expenses</p>
                </div>
              </div>
              {/* Progress bar */}
              <div className="mt-4">
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all ${
                      (summary?.run_rate_current || 0) >= 3 ? 'bg-emerald-400' : 
                      (summary?.run_rate_current || 0) >= 1 ? 'bg-brand-orange' : 'bg-red-400'
                    }`}
                    style={{ width: `${Math.min((summary?.run_rate_current || 0) / 12 * 100, 100)}%` }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/fixed-expenses" data-testid="run-rate-projected-link">
          <Card className="bg-brand-orange text-brand-navy cursor-pointer hover:shadow-xl transition-all">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-brand-navy/70 text-sm">Run Rate (Projected)</p>
                  <p className="text-2xl sm:text-3xl md:text-4xl font-bold mt-1">{summary?.run_rate_projected || 0}</p>
                  <p className="text-brand-navy/50 text-sm mt-1">months with receivables</p>
                </div>
                <div className="text-right">
                  <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-brand-navy/20 text-brand-navy">
                    +{((summary?.run_rate_projected || 0) - (summary?.run_rate_current || 0)).toFixed(1)} months
                  </div>
                  <p className="text-brand-navy/50 text-xs mt-2">Projected ÷ Monthly Expenses</p>
                </div>
              </div>
              {/* Progress bar */}
              <div className="mt-4">
                <div className="h-2 bg-brand-navy/20 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-brand-navy rounded-full transition-all"
                    style={{ width: `${Math.min((summary?.run_rate_projected || 0) / 12 * 100, 100)}%` }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Expense Breakdown Bar Chart */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold flex items-center justify-between">
              Expense Breakdown
              <Link to="/fixed-expenses">
                <Button variant="ghost" size="sm" className="text-brand-orange hover:text-brand-orange hover:bg-brand-orange/10 h-8 px-2">
                  View <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </Link>
            </CardTitle>
            <p className="text-sm text-slate-500">Monthly fixed expenses by category</p>
          </CardHeader>
          <CardContent>
            {expenseChartData.length > 0 ? (
              <div className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={expenseChartData}
                    layout="vertical"
                    margin={{ top: 10, right: 30, left: 10, bottom: 10 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#E2E8F0" />
                    <XAxis 
                      type="number" 
                      tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}k`}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#64748B', fontSize: 11 }}
                    />
                    <YAxis 
                      type="category" 
                      dataKey="name" 
                      width={100}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#334155', fontSize: 12 }}
                    />
                    <Tooltip
                      formatter={(value) => [formatCurrency(value), "Amount"]}
                      contentStyle={chartTooltipStyle}
                      labelStyle={{ color: "#F5A623", fontWeight: "bold", marginBottom: "4px" }}
                      itemStyle={{ color: "#FFFFFF" }}
                      cursor={{ fill: 'rgba(245, 166, 35, 0.1)' }}
                    />
                    <Bar 
                      dataKey="value" 
                      radius={[0, 6, 6, 0]}
                      maxBarSize={35}
                    >
                      {expenseChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[350px] flex items-center justify-center text-slate-400">
                <p>No expense data available</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* GST Summary */}
        <Card className="lg:col-span-1 bg-slate-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold text-brand-navy">GST at a Glance</CardTitle>
            <p className="text-sm text-slate-500">Tax collected vs Input tax credit</p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
                <p className="text-xs text-slate-500 mb-1">GST Collected</p>
                <p className="text-sm sm:text-xl font-bold text-emerald-600 tabular-nums">
                  {formatCurrency(summary?.gst_summary?.collected?.total || 0)}
                </p>
                <div className="mt-2 space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-400">CGST</span>
                    <span className="tabular-nums">{formatCurrency(summary?.gst_summary?.collected?.cgst || 0)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-400">SGST</span>
                    <span className="tabular-nums">{formatCurrency(summary?.gst_summary?.collected?.sgst || 0)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-400">IGST</span>
                    <span className="tabular-nums">{formatCurrency(summary?.gst_summary?.collected?.igst || 0)}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
                <p className="text-xs text-slate-500 mb-1">Input Tax Credit</p>
                <p className="text-sm sm:text-xl font-bold text-brand-navy tabular-nums">
                  {formatCurrency(summary?.gst_summary?.paid?.total || 0)}
                </p>
                <div className="mt-2 space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-400">CGST</span>
                    <span className="tabular-nums">{formatCurrency(summary?.gst_summary?.paid?.cgst || 0)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-400">SGST</span>
                    <span className="tabular-nums">{formatCurrency(summary?.gst_summary?.paid?.sgst || 0)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-400">IGST</span>
                    <span className="tabular-nums">{formatCurrency(summary?.gst_summary?.paid?.igst || 0)}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
                <p className="text-xs text-slate-500 mb-1">Pending GST</p>
                <div className="space-y-2 mt-1">
                  <div>
                    <p className="text-xs text-slate-400">Receivable</p>
                    <p className="text-sm sm:text-lg font-semibold text-emerald-600 tabular-nums">
                      {formatCurrency(summary?.gst_summary?.receivable?.total || 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Claimable ITC</p>
                    <p className="text-sm sm:text-lg font-semibold text-brand-navy tabular-nums">
                      {formatCurrency(summary?.gst_summary?.payable?.total || 0)}
                    </p>
                  </div>
                </div>
              </div>

              <div className={`rounded-xl p-4 shadow-sm border-2 ${
                (summary?.gst_summary?.net_liability || 0) > 0 
                  ? "bg-red-50 border-red-200" 
                  : "bg-emerald-50 border-emerald-200"
              }`}>
                <p className="text-xs text-slate-500 mb-1">Net GST to Govt</p>
                <p className={`text-lg sm:text-2xl font-bold tabular-nums ${
                  (summary?.gst_summary?.net_liability || 0) > 0 ? "text-red-600" : "text-emerald-600"
                }`}>
                  {formatCurrency(Math.abs(summary?.gst_summary?.net_liability || 0))}
                </p>
                <p className={`text-xs mt-2 ${
                  (summary?.gst_summary?.net_liability || 0) > 0 ? "text-red-500" : "text-emerald-500"
                }`}>
                  {(summary?.gst_summary?.net_liability || 0) > 0 
                    ? "Payable to government"
                    : (summary?.gst_summary?.net_liability || 0) < 0 
                      ? "ITC credit balance"
                      : "No liability"
                  }
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* TDS Summary */}
        <Card className="lg:col-span-1 bg-red-50/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold text-red-700">TDS at a Glance</CardTitle>
            <p className="text-sm text-slate-500">Tax Deducted at Source - payable to government</p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-xl p-4 shadow-sm border border-red-100">
                <p className="text-xs text-slate-500 mb-1">From Pending Payables</p>
                <p className="text-sm sm:text-xl font-bold text-red-600 tabular-nums">
                  {formatCurrency(summary?.tds_summary?.from_pending_payables || 0)}
                </p>
              </div>
              
              <div className="bg-white rounded-xl p-4 shadow-sm border border-red-100">
                <p className="text-xs text-slate-500 mb-1">From Paid Payables</p>
                <p className="text-sm sm:text-xl font-bold text-red-600 tabular-nums">
                  {formatCurrency(summary?.tds_summary?.from_paid_payables || 0)}
                </p>
              </div>

              <div className="col-span-2 bg-red-100 rounded-xl p-4 shadow-sm border-2 border-red-300">
                <p className="text-xs text-red-600 mb-1 font-medium">Total TDS Payable to Govt</p>
                <p className="text-lg sm:text-2xl font-bold text-red-700 tabular-nums">
                  {formatCurrency(summary?.tds_summary?.total_payable_to_govt || 0)}
                </p>
                <p className="text-xs text-red-500 mt-1">Must be deposited with government</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Projects Summary */}
      {projectSummary && projectSummary.active_count > 0 && (
        <Card data-testid="projects-summary-section">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <FolderKanban className="w-5 h-5 text-brand-orange" />
                Projects
              </CardTitle>
              <p className="text-sm text-slate-500">{projectSummary.active_count} active project{projectSummary.active_count > 1 ? 's' : ''} · Budget vs Spent</p>
            </div>
            <Link to="/projects">
              <Button variant="ghost" size="sm" className="text-brand-orange hover:text-brand-orange hover:bg-brand-orange/10 h-8 px-2">
                View All <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {/* Summary KPIs */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <div className="bg-brand-navy/5 rounded-xl p-3 text-center">
                <p className="text-xs text-slate-500">Budget</p>
                <p className="text-sm sm:text-lg font-bold text-brand-navy tabular-nums">{formatCurrency(projectSummary.total_budget)}</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-3 text-center">
                <p className="text-xs text-slate-500">Allocated</p>
                <p className="text-sm sm:text-lg font-bold text-blue-600 tabular-nums">{formatCurrency(projectSummary.total_allocated)}</p>
              </div>
              <div className="bg-brand-orange/10 rounded-xl p-3 text-center">
                <p className="text-xs text-slate-500">Spent</p>
                <p className="text-sm sm:text-lg font-bold text-brand-orange tabular-nums">{formatCurrency(projectSummary.total_spent)}</p>
              </div>
              <div className={`rounded-xl p-3 text-center ${(projectSummary.total_variance || 0) < 0 ? 'bg-red-50' : 'bg-emerald-50'}`}>
                <p className="text-xs text-slate-500">Variance</p>
                <p className={`text-sm sm:text-lg font-bold tabular-nums ${(projectSummary.total_variance || 0) < 0 ? 'text-red-600' : 'text-emerald-600'}`}>{formatCurrency(projectSummary.total_variance || 0)}</p>
              </div>
            </div>
            {/* Per-project breakdown */}
            <div className="space-y-3">
              {projectSummary.projects.map((p) => (
                <Link key={p.id} to="/projects" className="block" data-testid={`dashboard-project-${p.id}`}>
                  <div className="p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                    <div className="flex items-center justify-between mb-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{p.name}</p>
                        <p className="text-xs text-slate-500">{COMPANY_OPTIONS.find(c => c.value === p.company)?.label || p.company}</p>
                      </div>
                      <div className="text-right flex-shrink-0 ml-2">
                        <p className="text-sm font-bold tabular-nums text-brand-navy">{formatCurrency(p.total_budgeted)}</p>
                        <p className={`text-xs font-medium ${(p.variance || 0) < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                          {(p.variance || 0) < 0 ? `Overrun ${formatCurrency(Math.abs(p.variance))}` : `Under ${formatCurrency(p.variance || 0)}`}
                        </p>
                      </div>
                    </div>
                    <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${p.progress > 100 ? 'bg-red-500' : p.progress > 75 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                        style={{ width: `${Math.min(p.progress, 100)}%` }}
                      />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Company Breakdown & Bank Accounts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Company-wise Summary */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold">Company Overview</CardTitle>
            <p className="text-sm text-slate-500">Bank + Receivables - Payables = Net Position</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {COMPANY_OPTIONS.map((company) => {
                // Bank balance for this company
                const bankBalance = (summary?.bank_accounts || [])
                  .filter(acc => acc.company === company.value)
                  .reduce((sum, acc) => sum + acc.balance, 0);
                  
                // Receivables (pending invoices balance)
                const receivablesTotal = (summary?.recent_invoices || [])
                  .filter(inv => inv.company === company.value && inv.status !== 'paid')
                  .reduce((sum, inv) => {
                    const netReceivable = inv.net_receivable || inv.total_amount - (inv.tds_amount || 0);
                    const balance = inv.balance !== undefined ? inv.balance : netReceivable - (inv.amount_received || 0);
                    return sum + balance;
                  }, 0);
                  
                // Payables (pending payables balance)
                const payablesTotal = (summary?.recent_payables || [])
                  .filter(pay => pay.company === company.value && pay.status !== 'paid')
                  .reduce((sum, pay) => {
                    const netPayable = pay.net_payable || pay.total_amount - (pay.tds_amount || 0);
                    const balance = pay.balance !== undefined ? pay.balance : netPayable - (pay.amount_paid || 0);
                    return sum + balance;
                  }, 0);
                  
                // Net Position = Bank + Receivables - Payables
                const netPosition = bankBalance + receivablesTotal - payablesTotal;
                
                return (
                  <div 
                    key={company.value} 
                    className="p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors cursor-pointer"
                    onClick={() => setSelectedCompany(company.value)}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {company.logo && (
                          <img 
                            src={company.logo} 
                            alt={company.label} 
                            className="h-8 w-auto object-contain"
                          />
                        )}
                        <span className="font-medium text-slate-900">{company.label}</span>
                      </div>
                      <span className={`text-sm font-bold tabular-nums ${netPosition >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        Net: {netPosition >= 0 ? '+' : ''}{formatCurrency(netPosition)}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <p className="text-xs text-slate-400">Bank Balance</p>
                        <p className="text-sm font-semibold text-blue-600 tabular-nums">{formatCurrency(bankBalance)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Receivables</p>
                        <p className="text-sm font-semibold text-emerald-600 tabular-nums">+{formatCurrency(receivablesTotal)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Payables</p>
                        <p className="text-sm font-semibold text-red-600 tabular-nums">-{formatCurrency(payablesTotal)}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Bank Accounts */}
        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg font-semibold">Bank Accounts</CardTitle>
              <p className="text-sm text-slate-500">Account balances</p>
            </div>
            <Link to="/bank-accounts">
              <Button variant="ghost" size="sm" className="text-brand-orange hover:text-brand-orange hover:bg-brand-orange/10">
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {summary?.bank_accounts?.length > 0 ? (
              <div className="space-y-3">
                {summary.bank_accounts.map((account, index) => (
                  <div
                    key={account.id}
                    className="flex items-center justify-between p-3 sm:p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors gap-2"
                  >
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                      <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center flex-shrink-0`} style={{ backgroundColor: `${CHART_COLORS[index % CHART_COLORS.length]}20` }}>
                        <Building2 className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: CHART_COLORS[index % CHART_COLORS.length] }} />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 text-sm truncate">{account.name}</p>
                        <p className="text-xs text-slate-500 truncate">{account.bank_name} • {account.account_type}</p>
                      </div>
                    </div>
                    <p className="font-bold text-slate-900 tabular-nums text-sm sm:text-lg flex-shrink-0">
                      {formatCurrency(account.balance)}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <Building2 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No bank accounts added</p>
                <Link to="/bank-accounts">
                  <Button variant="link" className="text-brand-orange mt-2">Add account</Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* One-time Expenses & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* One-time Expenses */}
        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg font-semibold">One-time Expenses</CardTitle>
              <p className="text-sm text-slate-500">Non-recurring costs</p>
            </div>
            <Link to="/variable-expenses">
              <Button variant="ghost" size="sm" className="text-brand-orange hover:text-brand-orange hover:bg-brand-orange/10 h-8 px-2">
                View <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-50 rounded-xl p-3">
                  <p className="text-xs text-slate-500">Total</p>
                  <p className="text-sm sm:text-xl font-bold text-slate-900 tabular-nums">
                    {formatCurrency(summary?.onetime_expenses?.total || 0)}
                  </p>
                </div>
                <div className="bg-orange-50 rounded-xl p-3">
                  <p className="text-xs text-slate-500">This Month</p>
                  <p className="text-sm sm:text-xl font-bold text-orange-600 tabular-nums">
                    {formatCurrency(summary?.onetime_expenses?.this_month || 0)}
                  </p>
                </div>
              </div>
              
              {summary?.onetime_expenses?.recent?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-slate-500">Recent</p>
                  {summary.onetime_expenses.recent.slice(0, 3).map((expense) => (
                    <div key={expense.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                      <div>
                        <p className="text-sm font-medium text-slate-900">{expense.name}</p>
                        <p className="text-xs text-slate-500">{formatDate(expense.expense_date)}</p>
                      </div>
                      <p className="text-sm font-semibold text-slate-700 tabular-nums">
                        {formatCurrency(expense.amount)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Invoices */}
        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg font-semibold">Recent Invoices</CardTitle>
              <p className="text-sm text-slate-500">Base + GST - TDS = Net Receivable</p>
            </div>
            <Link to="/invoices">
              <Button variant="ghost" size="sm" className="text-brand-orange hover:text-brand-orange hover:bg-brand-orange/10 h-8 px-2">
                View <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {summary?.recent_invoices?.length > 0 ? (
              <div className="space-y-4">
                {summary.recent_invoices.slice(0, 4).map((invoice) => {
                  const baseAmount = invoice.base_amount || 0;
                  const gstAmount = (invoice.cgst_amount || 0) + (invoice.sgst_amount || 0) + (invoice.igst_amount || 0);
                  const totalWithGst = invoice.total_amount || (baseAmount + gstAmount);
                  const tdsAmount = invoice.tds_amount || 0;
                  const netReceivable = invoice.net_receivable || (totalWithGst - tdsAmount);
                  
                  return (
                    <div key={invoice.id} className="p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{invoice.client_name}</p>
                          <p className="text-xs text-slate-500">{invoice.invoice_number}</p>
                        </div>
                        <StatusBadge status={invoice.status} />
                      </div>
                      <div className="grid grid-cols-4 gap-2 text-xs">
                        <div>
                          <p className="text-slate-400">Base</p>
                          <p className="font-semibold text-slate-700 tabular-nums">{formatCurrency(baseAmount)}</p>
                        </div>
                        <div>
                          <p className="text-slate-400">GST</p>
                          <p className="font-semibold text-blue-600 tabular-nums">+{formatCurrency(gstAmount)}</p>
                        </div>
                        <div>
                          <p className="text-slate-400">TDS</p>
                          <p className="font-semibold text-red-600 tabular-nums">-{formatCurrency(tdsAmount)}</p>
                        </div>
                        <div>
                          <p className="text-slate-400">Net Recv</p>
                          <p className="font-bold text-emerald-600 tabular-nums">{formatCurrency(netReceivable)}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-6 text-slate-400">
                <p className="text-sm">No invoices yet</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Payables */}
        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg font-semibold">Recent Payables</CardTitle>
              <p className="text-sm text-slate-500">Base + GST - TDS = Net Payable</p>
            </div>
            <Link to="/payables">
              <Button variant="ghost" size="sm" className="text-brand-orange hover:text-brand-orange hover:bg-brand-orange/10 h-8 px-2">
                View <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {summary?.recent_payables?.length > 0 ? (
              <div className="space-y-4">
                {summary.recent_payables.slice(0, 4).map((payable) => {
                  const baseAmount = payable.base_amount || 0;
                  const gstAmount = (payable.cgst_amount || 0) + (payable.sgst_amount || 0) + (payable.igst_amount || 0);
                  const totalWithGst = payable.total_amount || (baseAmount + gstAmount);
                  const tdsAmount = payable.tds_amount || 0;
                  const netPayable = payable.net_payable || (totalWithGst - tdsAmount);
                  
                  return (
                    <div key={payable.id} className="p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{payable.vendor_name}</p>
                          <p className="text-xs text-slate-500">Due: {formatDate(payable.due_date)}</p>
                        </div>
                        <StatusBadge status={payable.status} />
                      </div>
                      <div className="grid grid-cols-4 gap-2 text-xs">
                        <div>
                          <p className="text-slate-400">Base</p>
                          <p className="font-semibold text-slate-700 tabular-nums">{formatCurrency(baseAmount)}</p>
                        </div>
                        <div>
                          <p className="text-slate-400">GST</p>
                          <p className="font-semibold text-blue-600 tabular-nums">+{formatCurrency(gstAmount)}</p>
                        </div>
                        <div>
                          <p className="text-slate-400">TDS</p>
                          <p className="font-semibold text-emerald-600 tabular-nums">-{formatCurrency(tdsAmount)}</p>
                        </div>
                        <div>
                          <p className="text-slate-400">Net Pay</p>
                          <p className="font-bold text-red-600 tabular-nums">{formatCurrency(netPayable)}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-6 text-slate-400">
                <p className="text-sm">No payables yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Statutory Monthly Modal */}
      <Dialog open={!!statutoryModal} onOpenChange={(open) => { if (!open) setStatutoryModal(null); }}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="statutory-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-brand-orange" />
              {statutoryModal === "tds" ? "TDS Payable — Monthly" : "GST Payable — Monthly"}
            </DialogTitle>
          </DialogHeader>
          {statutoryData && statutoryModal === "tds" && (
            <div className="space-y-3 mt-2">
              {statutoryData.tds.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">No TDS payable entries found</p>
              ) : statutoryData.tds.map((item) => (
                <div key={item.month} className={`border rounded-lg p-3 ${item.status === "paid" ? "bg-emerald-50 border-emerald-200" : "bg-white border-slate-200"}`} data-testid={`tds-month-${item.month}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-semibold text-sm">{new Date(item.month + "-01").toLocaleDateString("en-IN", { month: "long", year: "numeric" })}</p>
                      <p className={`text-sm sm:text-lg font-bold tabular-nums ${item.status === "paid" ? "text-emerald-600 line-through" : "text-red-600"}`}>{formatCurrency(item.amount)}</p>
                      {item.status === "paid" && item.paid_date && (
                        <p className="text-xs text-emerald-600">Paid on {item.paid_date}</p>
                      )}
                    </div>
                    {canEdit && (
                      <div className="flex gap-1">
                        {item.status === "paid" ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleTogglePaid("tds", item.month)}
                            className="border-amber-300 text-amber-700 hover:bg-amber-50"
                            data-testid={`undo-tds-${item.month}`}
                          >
                            Undo
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => handleTdsMarkPaid(item.month)}
                            className="bg-red-600 hover:bg-red-700 text-white"
                            data-testid={`toggle-tds-${item.month}`}
                          >
                            Mark Paid
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="space-y-1">
                    {item.details.map((d, i) => (
                      <div key={`${d.vendor}-${d.tds_rate}-${i}`} className="flex justify-between text-xs text-slate-500">
                        <span>{d.vendor} — TDS {d.tds_rate}%{d.source === "fixed" ? " (Fixed)" : d.source === "variable" ? " (Variable)" : ""}</span>
                        <span className="tabular-nums">{formatCurrency(d.tds_amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* TDS Bank Account Selector Dialog */}
              {tdsBankSelector.open && (
                <div className="border-2 border-brand-orange rounded-lg p-4 bg-orange-50 space-y-3" data-testid="tds-bank-selector">
                  <p className="font-semibold text-sm">Select bank account for TDS payment</p>
                  <Select value={tdsBankSelector.bankId || "none"} onValueChange={(v) => setTdsBankSelector(prev => ({ ...prev, bankId: v === "none" ? "" : v }))}>
                    <SelectTrigger data-testid="tds-bank-select"><SelectValue placeholder="Select bank account" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select bank account</SelectItem>
                      {bankAccounts.map(b => (
                        <SelectItem key={b.id} value={b.id}>{b.name} — {formatCurrency(b.balance)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex gap-2 justify-end">
                    <Button size="sm" variant="outline" onClick={() => setTdsBankSelector({ open: false, month: null, bankId: "" })}>Cancel</Button>
                    <Button size="sm" onClick={confirmTdsPaid} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-tds-paid">Confirm & Deduct</Button>
                  </div>
                </div>
              )}
            </div>
          )}
          {statutoryData && statutoryModal === "gst" && (
            <div className="space-y-3 mt-2">
              {statutoryData.gst.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">No GST payable entries found</p>
              ) : statutoryData.gst.map((item) => (
                <div key={item.month} className={`border rounded-lg p-3 ${item.status === "paid" ? "bg-emerald-50 border-emerald-200" : "bg-white border-slate-200"}`} data-testid={`gst-month-${item.month}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-semibold text-sm">{new Date(item.month + "-01").toLocaleDateString("en-IN", { month: "long", year: "numeric" })}</p>
                      <p className={`text-sm sm:text-lg font-bold tabular-nums ${item.status === "paid" ? "text-emerald-600 line-through" : item.net_amount > 0 ? "text-red-600" : "text-emerald-600"}`}>{formatCurrency(item.net_amount)}</p>
                      {item.status === "paid" && item.paid_date && (
                        <p className="text-xs text-emerald-600">Paid on {item.paid_date}</p>
                      )}
                    </div>
                    {canEdit && item.net_amount > 0 && (
                      item.status === "paid" ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleTogglePaid("gst", item.month)}
                          className="border-amber-300 text-amber-700 hover:bg-amber-50"
                          data-testid={`undo-gst-${item.month}`}
                        >
                          Undo
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => handleTogglePaid("gst", item.month)}
                          className="bg-red-600 hover:bg-red-700 text-white"
                          data-testid={`toggle-gst-${item.month}`}
                        >
                          Mark Paid
                        </Button>
                      )
                    )}
                  </div>
                  <div className="flex justify-between text-xs text-slate-500 mb-1">
                    <span>Collected (Output)</span><span className="tabular-nums">{formatCurrency(item.collected)}</span>
                  </div>
                  <div className="flex justify-between text-xs text-slate-500 mb-1">
                    <span>ITC (Input)</span><span className="tabular-nums">-{formatCurrency(item.itc)}</span>
                  </div>
                  <div className="flex justify-between text-xs font-medium border-t pt-1">
                    <span>Net to Govt</span><span className="tabular-nums">{formatCurrency(item.net_amount)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent className="max-w-md" data-testid="export-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileDown className="w-5 h-5 text-brand-orange" />
              Export Dashboard Summary
            </DialogTitle>
            <DialogDescription>
              Choose format, time period, and scope for your export
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Format Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Export Format</Label>
              <RadioGroup 
                value={exportFormat} 
                onValueChange={setExportFormat}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="pdf" id="format-pdf" />
                  <Label htmlFor="format-pdf" className="cursor-pointer">PDF</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="csv" id="format-csv" />
                  <Label htmlFor="format-csv" className="cursor-pointer">CSV</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Period Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Time Period</Label>
              <Select value={exportPeriod} onValueChange={setExportPeriod}>
                <SelectTrigger data-testid="export-period-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="current_month">Current Month</SelectItem>
                  <SelectItem value="current_quarter">Current Quarter</SelectItem>
                  <SelectItem value="current_fy">Current Financial Year (Apr - Mar)</SelectItem>
                  <SelectItem value="previous_fy">Previous Financial Year</SelectItem>
                  <SelectItem value="custom">Custom Date Range</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Custom Date Range */}
              {exportPeriod === "custom" && (
                <div className="grid grid-cols-2 gap-3 pt-2">
                  <div>
                    <Label htmlFor="start-date" className="text-xs text-slate-500">Start Date</Label>
                    <DatePicker
                      value={customStartDate}
                      onChange={setCustomStartDate}
                      data-testid="export-start-date"
                    />
                  </div>
                  <div>
                    <Label htmlFor="end-date" className="text-xs text-slate-500">End Date</Label>
                    <DatePicker
                      value={customEndDate}
                      onChange={setCustomEndDate}
                      data-testid="export-end-date"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Scope Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Export Scope</Label>
              <Select value={exportScope} onValueChange={setExportScope}>
                <SelectTrigger data-testid="export-scope-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="summary">Summary (All Companies Total)</SelectItem>
                  <SelectItem value="company_wise">Company-wise Breakdown</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Company Filter */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Company</Label>
              <Select value={exportCompany} onValueChange={setExportCompany}>
                <SelectTrigger data-testid="export-company-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Companies</SelectItem>
                  <SelectItem value="empact-studio">Empact Studio</SelectItem>
                  <SelectItem value="event-resource">Event Resource</SelectItem>
                  <SelectItem value="vijay-sabhlok">Vijay Sabhlok</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setExportDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleExport}
              disabled={exporting}
              className="bg-brand-orange hover:bg-[#e09515] text-brand-navy gap-2"
              data-testid="export-confirm-btn"
            >
              {exporting ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Export
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
